import { createAxiosInstance } from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';
const LOT_REQUEST_API_URL = `${API_BASE_URL}/lot-requests`;

// Create axios instance with default config and interceptors
const api = createAxiosInstance({
  baseURL: LOT_REQUEST_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const lotRequestService = {
  // Basic CRUD operations
  createLotRequest: async (lotRequest) => {
    try {
      const response = await api.post('', lotRequest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create lot request');
    }
  },

  getLotRequestById: async (id) => {
    try {
      const response = await api.get(`/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch lot request');
    }
  },

  getAllLotRequests: async (page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc') => {
    try {
      const response = await api.get('', {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch lot requests');
    }
  },

  updateLotRequest: async (id, lotRequest) => {
    try {
      const response = await api.put(`/${id}`, lotRequest);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update lot request');
    }
  },

  deleteLotRequest: async (id) => {
    try {
      await api.delete(`/${id}`);
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete lot request');
    }
  },

  // Customer operations
  getLotRequestsByCustomer: async (customerId, page = 0, size = 10) => {
    try {
      const response = await api.get(`/customer/${customerId}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch customer lot requests');
    }
  },

  // Seller operations
  getLotRequestsBySeller: async (sellerId, page = 0, size = 10) => {
    try {
      const response = await api.get(`/seller/${sellerId}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch seller lot requests');
    }
  },

  // Status management
  updateStatus: async (id, status, updatedBy, notes = '') => {
    try {
      const response = await api.put(`/${id}/status`, null, {
        params: { status, updatedBy, notes }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update status');
    }
  },

  getLotRequestsByStatus: async (status, page = 0, size = 10) => {
    try {
      const response = await api.get(`/status/${status}`, {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch lot requests by status');
    }
  },

  // Priority management
  updatePriority: async (id, priority, updatedBy, reason = '') => {
    try {
      const response = await api.put(`/${id}/priority`, null, {
        params: { priority, updatedBy, reason }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update priority');
    }
  },

  getLotRequestsByPriority: async (priority) => {
    try {
      const response = await api.get(`/priority/${priority}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch lot requests by priority');
    }
  },

  // Inspection operations
  scheduleInspection: async (id, inspectionDate, inspectorId, notes = '') => {
    try {
      const response = await api.post(`/${id}/schedule-inspection`, null, {
        params: { inspectionDate, inspectorId, notes }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to schedule inspection');
    }
  },

  completeInspection: async (id, inspectionResult, inspectionNotes = '', inspectorId) => {
    try {
      const response = await api.post(`/${id}/complete-inspection`, null, {
        params: { inspectionResult, inspectionNotes, inspectorId }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to complete inspection');
    }
  },

  getRequestsPendingInspection: async () => {
    try {
      const response = await api.get('/pending-inspection');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pending inspection requests');
    }
  },

  // Approval operations
  approveLotRequest: async (id, approvedBy, approvalNotes = '') => {
    try {
      const response = await api.post(`/${id}/approve`, null, {
        params: { approvedBy, approvalNotes }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to approve lot request');
    }
  },

  rejectLotRequest: async (id, rejectedBy, rejectionReason) => {
    try {
      const response = await api.post(`/${id}/reject`, null, {
        params: { rejectedBy, rejectionReason }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reject lot request');
    }
  },

  getApprovedRequests: async () => {
    try {
      const response = await api.get('/approved');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch approved requests');
    }
  },

  // Delivery operations
  markReadyForDelivery: async (id, preparedBy, deliveryNotes = '') => {
    try {
      const response = await api.post(`/${id}/ready-for-delivery`, null, {
        params: { preparedBy, deliveryNotes }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark ready for delivery');
    }
  },

  markInTransit: async (id, trackingNumber, carrier, shippedBy) => {
    try {
      const response = await api.post(`/${id}/in-transit`, null, {
        params: { trackingNumber, carrier, shippedBy }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark in transit');
    }
  },

  markDelivered: async (id, deliveryDate, deliveredBy, deliveryNotes = '') => {
    try {
      const response = await api.post(`/${id}/delivered`, null, {
        params: { deliveryDate, deliveredBy, deliveryNotes }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark delivered');
    }
  },

  getRequestsReadyForDelivery: async () => {
    try {
      const response = await api.get('/ready-for-delivery');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch requests ready for delivery');
    }
  },

  // Search operations
  searchLotRequests: async (searchTerm, page = 0, size = 10) => {
    try {
      const response = await api.get('/search', {
        params: { searchTerm, page, size }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search lot requests');
    }
  },

  searchWithFilters: async (searchTerm, status = null, priority = null, page = 0, size = 10) => {
    try {
      const params = { searchTerm, page, size };
      if (status) params.status = status;
      if (priority) params.priority = priority;
      
      const response = await api.get('/search/advanced', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to search with filters');
    }
  },

  // Priority requests
  getUrgentActiveRequests: async () => {
    try {
      const response = await api.get('/urgent');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch urgent requests');
    }
  },

  getHighPriorityPendingRequests: async () => {
    try {
      const response = await api.get('/high-priority-pending');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch high priority pending requests');
    }
  },

  getOverdueRequests: async () => {
    try {
      const response = await api.get('/overdue');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch overdue requests');
    }
  },

  // Category operations
  getLotRequestsByCategory: async (category) => {
    try {
      const response = await api.get(`/category/${category}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch lot requests by category');
    }
  },

  getLotRequestsByCategoryAndSubCategory: async (category, subCategory) => {
    try {
      const response = await api.get(`/category/${category}/subcategory/${subCategory}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch lot requests by category and subcategory');
    }
  },

  // Analytics
  getStatusCounts: async () => {
    try {
      const response = await api.get('/analytics/status-counts');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch status counts');
    }
  },

  getPriorityCounts: async () => {
    try {
      const response = await api.get('/analytics/priority-counts');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch priority counts');
    }
  },

  getDashboardData: async () => {
    try {
      const response = await api.get('/analytics/dashboard');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch dashboard data');
    }
  },

  getSellerDashboardData: async (sellerId) => {
    try {
      const response = await api.get(`/analytics/seller/${sellerId}/dashboard`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch seller dashboard data');
    }
  },

  getCustomerDashboardData: async (customerId) => {
    try {
      const response = await api.get(`/analytics/customer/${customerId}/dashboard`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch customer dashboard data');
    }
  },

  // Bulk operations
  bulkUpdateStatus: async (requestIds, status, updatedBy) => {
    try {
      const response = await api.put('/bulk/status', requestIds, {
        params: { status, updatedBy }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk update status');
    }
  },

  bulkUpdatePriority: async (requestIds, priority, updatedBy) => {
    try {
      const response = await api.put('/bulk/priority', requestIds, {
        params: { priority, updatedBy }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk update priority');
    }
  },

  bulkDelete: async (requestIds) => {
    try {
      await api.delete('/bulk', { data: requestIds });
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk delete');
    }
  },

  // Timeline operations
  updateTimeline: async (id, timeline, updatedBy, reason = '') => {
    try {
      const response = await api.put(`/${id}/timeline`, null, {
        params: { timeline, updatedBy, reason }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update timeline');
    }
  },

  getRequestsWithUpcomingDeadlines: async (days = 7) => {
    try {
      const response = await api.get('/upcoming-deadlines', {
        params: { days }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch upcoming deadlines');
    }
  },

  // Inspector operations
  getRequestsByInspector: async (inspectorId) => {
    try {
      const response = await api.get(`/inspector/${inspectorId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch inspector requests');
    }
  },

  assignInspector: async (id, inspectorId, assignedBy) => {
    try {
      const response = await api.put(`/${id}/assign-inspector`, null, {
        params: { inspectorId, assignedBy }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to assign inspector');
    }
  },

  // Product operations
  getLotRequestsByProduct: async (productId) => {
    try {
      const response = await api.get(`/product/${productId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch product lot requests');
    }
  },

  // Demo data operations
  initializeDemoData: async () => {
    try {
      const response = await api.post('/demo/initialize');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to initialize demo data');
    }
  },

  clearDemoData: async () => {
    try {
      const response = await api.delete('/demo/clear');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to clear demo data');
    }
  },

  getDemoDataStatus: async () => {
    try {
      const response = await api.get('/demo/status');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get demo data status');
    }
  },

  // Utility functions
  formatDate: (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  },

  formatDateTime: (dateTime) => {
    if (!dateTime) return '';
    return new Date(dateTime).toLocaleString();
  },

  getStatusColor: (status) => {
    const statusColors = {
      'PENDING_INSPECTION': '#ff9800',
      'UNDER_REVIEW': '#2196f3',
      'LOT_APPROVED': '#4caf50',
      'LOT_REJECTED': '#f44336',
      'READY_FOR_DELIVERY': '#9c27b0',
      'IN_TRANSIT': '#ff5722',
      'DELIVERED': '#4caf50',
      'CANCELLED': '#757575',
      'ON_HOLD': '#ffc107'
    };
    return statusColors[status] || '#757575';
  },

  getPriorityColor: (priority) => {
    const priorityColors = {
      'LOW': '#4caf50',
      'MEDIUM': '#ff9800',
      'HIGH': '#f44336',
      'URGENT': '#9c27b0'
    };
    return priorityColors[priority] || '#757575';
  },

  getStatusDisplayName: (status) => {
    const statusNames = {
      'PENDING_INSPECTION': 'Pending Inspection',
      'UNDER_REVIEW': 'Under Review',
      'LOT_APPROVED': 'Lot Approved',
      'LOT_REJECTED': 'Lot Rejected',
      'READY_FOR_DELIVERY': 'Ready for Delivery',
      'IN_TRANSIT': 'In Transit',
      'DELIVERED': 'Delivered',
      'CANCELLED': 'Cancelled',
      'ON_HOLD': 'On Hold'
    };
    return statusNames[status] || status;
  },

  getPriorityDisplayName: (priority) => {
    const priorityNames = {
      'LOW': 'Low',
      'MEDIUM': 'Medium',
      'HIGH': 'High',
      'URGENT': 'Urgent'
    };
    return priorityNames[priority] || priority;
  }
};

export default lotRequestService;